import { useQuery } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { gql } from 'src/__generated__';
import type { UnconfirmedDataCompleted } from 'src/__generated__/graphql';
import type { ResumeFieldsValues } from 'src/components/ResumeFields/ResumeFields';
import { logError } from 'src/modules/error-logger/browserLog';

const POLL_INTERVAL = 1000;
const MAX_POLLING_ATTEMPTS = 8;

const UNCONFIRMED_DATA_QUERY = gql(`
  query UnconfirmedDataQuery(
    $contextId: String!
    $languageCode: LanguageCodeIso!
  ) {
    viewer {
      _id
      unconfirmedDataForContext(
        contextId: $contextId
        contextType: Application
        languageCode: $languageCode
      ) {
        ... on UnconfirmedDataPending {
          completed
        }
        ... on UnconfirmedDataCompleted {
          completed
          roles {
            ...unconfirmedRole
          }
          qualifications {
            ...unconfirmedQualification
          }
        }
      }
    }
  }
`);

export const useUnconfirmedData = ({ skip = false }) => {
  const count = useRef(MAX_POLLING_ATTEMPTS);
  const interval = useRef<ReturnType<typeof setInterval>>(undefined);
  const { language } = useMelwaysInfo();

  // Get resumeId from the form
  const { watch } = useFormContext<ResumeFieldsValues>();
  const resumeId = watch('resume-select');

  const { data, refetch, loading, error } = useQuery(UNCONFIRMED_DATA_QUERY, {
    skip: skip || !resumeId,
    variables: {
      contextId: resumeId,
      languageCode: language,
    },
    onError(err) {
      metrics.count('resume-parsing-unexpected-error');
      logError({ ...err, type: 'unconfirmed-data' }, { error: err });
    },
  });

  const completed = Boolean(data?.viewer?.unconfirmedDataForContext.completed);

  // pollInterval doesn't work reliably with useQuery, hence this workaround
  // https://github.com/apollographql/apollo-client/issues/5531
  useEffect(() => {
    if (completed || skip || !resumeId) return;

    interval.current = setInterval(() => {
      refetch();
      count.current -= 1;
      if (count.current === 0) {
        clearInterval(interval.current);
        metrics.count('resume-parsing-timeout');
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval.current);
  }, [refetch, completed, skip, resumeId]);

  useEffect(() => {
    if (completed) {
      metrics.count('resume-parsing-success');
      clearInterval(interval.current);
    }
  }, [completed]);

  if (skip || !resumeId) {
    return { error, loading: false, roles: [], qualifications: [] };
  }

  const context = data?.viewer?.unconfirmedDataForContext as
    | UnconfirmedDataCompleted
    | undefined;

  return {
    error,
    loading,
    roles: context?.roles || [],
    qualifications: context?.qualifications || [],
  };
};
